import { HSComponent } from './component.js';
import { HelpRegistry } from './help-registry.js';
import { escapeHtml } from './markdown.js';
import { HELP_ENTRY_STYLES, renderEntryBody, renderDraftBadge } from './help-render.js';
import styles from './concept-panel.css';
import template from './concept-panel.html';

const CONCEPT_PREFIX = 'concept.';

// Docked "Concepts" browser for the tier-2 help system
// (docs/PIPELINE_SANDBOX_HELP_SYSTEM.md §9/§10). Unlike <hs-help-modal>
// (transient, opened from a single form field), this panel is meant to stay
// open while the developer edits the pipeline it explains, so it toggles
// visibility rather than mounting/unmounting on close, and never places a
// backdrop over the rest of the sandbox.
export class HSConceptPanel extends HSComponent {
    #stack = [];   // [{ key, entry }, ...] — empty = concept index view

    onLoad() {
        this.loadTemplate(template);
        this.loadStyles(styles);
        this.loadStyles(HELP_ENTRY_STYLES);
        this.onEvent('click', () => this.#goHome(), this.shadowRoot.querySelector('.cp-home-btn'));
        this.onEvent('click', () => this.toggle(false), this.shadowRoot.querySelector('.cp-close-btn'));
        this.onEvent('click', (_, ev) => {
            const crumb = ev.target.closest('[data-crumb-index]');
            if (crumb) this.#goToCrumb(Number(crumb.dataset.crumbIndex));
        }, this.shadowRoot.querySelector('.cp-breadcrumb'));
        this.onEvent('click', (_, ev) => {
            const navBtn = ev.target.closest('[data-help-nav-key]');
            if (navBtn) this.#navigateTo(navBtn.dataset.helpNavKey);
        }, this.shadowRoot.querySelector('.cp-body'));
        this.#render();
    }

    get isOpen() {
        return this.shadowRoot.querySelector('.cp-drawer')?.classList.contains('open') ?? false;
    }

    toggle(force) {
        const drawer = this.shadowRoot.querySelector('.cp-drawer');
        const willOpen = typeof force === 'boolean' ? force : !drawer.classList.contains('open');
        drawer.classList.toggle('open', willOpen);
        if (willOpen) this.#updateDeepLinkHash();
        else this.#clearDeepLinkHash();
    }

    // Deep-link entry point (task 5): jump straight to a key and open the
    // panel. Unresolvable keys are a silent no-op — same rule as see_also.
    openKey(key) {
        const entry = HelpRegistry.getEntry(key);
        if (!entry) return;
        this.#stack.push({ key, entry });
        this.#render();
        this.toggle(true);
    }

    #goHome() {
        this.#stack = [];
        this.#render();
        this.#updateDeepLinkHash();
    }

    #navigateTo(key) {
        const entry = HelpRegistry.getEntry(key);
        if (!entry) return;
        this.#stack.push({ key, entry });
        this.#render();
        this.#updateDeepLinkHash();
    }

    #goToCrumb(index) {
        if (index < 0) {
            this.#goHome();
            return;
        }
        this.#stack = this.#stack.slice(0, index + 1);
        this.#render();
        this.#updateDeepLinkHash();
    }

    #updateDeepLinkHash() {
        const current = this.#stack[this.#stack.length - 1];
        const hash = current ? `#help=${encodeURIComponent(current.key)}` : '';
        history.replaceState(null, '', `${location.pathname}${location.search}${hash}`);
    }

    #clearDeepLinkHash() {
        if (!location.hash.startsWith('#help=')) return;
        history.replaceState(null, '', `${location.pathname}${location.search}`);
    }

    #render() {
        this.shadowRoot.querySelector('.cp-breadcrumb').innerHTML = this.#renderBreadcrumb();
        this.shadowRoot.querySelector('.cp-body').innerHTML = this.#stack.length
            ? this.#renderDetail()
            : this.#renderIndex();
    }

    #renderBreadcrumb() {
        if (!this.#stack.length) return '';
        const crumbs = ['<button type="button" class="cp-crumb" data-crumb-index="-1">Concepts</button>'];
        this.#stack.forEach(({ key }, i) => {
            const isLast = i === this.#stack.length - 1;
            crumbs.push('<span>\u203a</span>');
            crumbs.push(isLast
                ? `<span class="cp-crumb-current">${escapeHtml(key)}</span>`
                : `<button type="button" class="cp-crumb" data-crumb-index="${i}">${escapeHtml(key)}</button>`);
        });
        return crumbs.join('');
    }

    #renderIndex() {
        const concepts = HelpRegistry.getEntriesByPrefix(CONCEPT_PREFIX);
        if (!concepts.length) {
            return '<div class="cp-empty">No concept help is available yet.</div>';
        }
        const items = concepts.map(({ key, entry }) => `
            <button type="button" class="cp-index-item" data-help-nav-key="${escapeHtml(key)}">
                <div class="cp-index-key">${escapeHtml(key)}${renderDraftBadge(entry)}</div>
                <div class="cp-index-short">${escapeHtml(entry.short || '')}</div>
            </button>
        `).join('');
        return `<div class="cp-index-intro">Cross-cutting concepts — click one to read more.</div>${items}`;
    }

    #renderDetail() {
        const { key, entry } = this.#stack[this.#stack.length - 1];
        return `
            <div class="cp-detail-title-row">
                <span class="cp-detail-title">${escapeHtml(key)}</span>
                ${renderDraftBadge(entry)}
            </div>
            ${renderEntryBody(entry)}
        `;
    }
}

customElements.define('hs-concept-panel', HSConceptPanel);
